import { useCallback, useEffect, useState } from 'react'
import { Topbar, Sidebar, ReversalItem } from "../../components";
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken } from '../../slices/staffSlice';
import { authStaff } from '../../utils/constants';
import { fetchRequests } from '../../Requests/getRequests';
import { show } from '../../slices/toastSlice';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { RepeatIcon, Search01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';

const TABS = [['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']];

function Index() {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [tab, setTab] = useState('pending');
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [field, setField] = useState('new'); // which transaction id to search: 'new' | 'old'
    const [data, setData] = useState({ items: [], total: 0, pages: 1 });
    const [loading, setLoading] = useState(true);

    const searching = search.length > 0;

    // debounce the search box, and reset to page 1 whenever the term changes
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const load = useCallback(async () => {
        setLoading(true);
        // while searching we span every status; otherwise we list the selected tab
        const params = new URLSearchParams({ page: String(page) });
        if (search) {
            params.set('search', search);
            params.set('field', field);
        } else {
            params.set('tab', tab);
        }
        const res = await fetchRequests(`/loans/staff/reversals?${params.toString()}`, "GET", authStaff(accessToken), {}, {});
        setLoading(false);

        if (res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }

        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res?.err }));
            return;
        }

        setData({ items: res?.items || [], total: res?.total || 0, pages: res?.pages || 1 });
    }, [accessToken, tab, page, search, field, dispatch, navigate]);

    useEffect(() => { load(); }, [load]);

    const changeTab = (value) => {
        if (value === tab) return;
        setTab(value);
        setPage(1);
    };

    const changeField = (value) => {
        setField(value);
        if (search) setPage(1); // only affects the query while a term is active
    };

    return (
        <div className='main-container'>
            <Topbar />
            <Sidebar />

            <main className='main-content'>
                <section className='section-container space-y-6'>

                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
                                <HugeiconsIcon icon={RepeatIcon} size={18} className="text-amber-600" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Transaction Reversal Requests</h1>
                                <p className="text-sm text-gray-400">
                                    {searching ? (
                                        <>
                                            {data.total} {data.total === 1 ? 'result' : 'results'} across all statuses
                                            <span> matching “{search}”</span>
                                        </>
                                    ) : (
                                        <>{data.total} {tab} {data.total === 1 ? 'request' : 'requests'}</>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-stretch gap-2 w-full sm:w-auto">
                            <select
                                value={field}
                                onChange={(e) => changeField(e.target.value)}
                                className="shrink-0 py-2 pl-3 pr-8 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-colors"
                                aria-label="Search field"
                            >
                                <option value="new">New Transaction ID</option>
                                <option value="old">Old Transaction ID</option>
                            </select>

                            <div className="relative flex-1 sm:w-64">
                                <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                    <HugeiconsIcon icon={Search01Icon} size={16} className="text-gray-400" />
                                </span>
                                <input
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="Search transaction ID"
                                    className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-colors"
                                />
                                {searchInput && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchInput('')}
                                        className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        aria-label="Clear search"
                                    >
                                        <HugeiconsIcon icon={Cancel01Icon} size={15} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tabs — status filter applies only when not searching */}
                    <div className="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700">
                        <div className={`flex gap-1 ${searching ? 'opacity-40 pointer-events-none' : ''}`}>
                            {TABS.map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    disabled={searching}
                                    onClick={() => changeTab(value)}
                                    className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors ${tab === value
                                        ? 'border-amber-500 text-amber-600'
                                        : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        {searching && (
                            <span className="text-xs text-gray-400 pb-2">Searching all statuses</span>
                        )}
                    </div>

                    {loading ? (
                        <div className="py-16 flex justify-center">
                            <svg className="animate-spin w-6 h-6 text-amber-500" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                        </div>
                    ) : data.items.length > 0 ? (
                        <div className="space-y-3">
                            {data.items.map((rev) => (
                                <ReversalItem
                                    key={rev?._id}
                                    item={rev}
                                    onActionDone={load}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-12 text-center">
                            <HugeiconsIcon icon={searching ? Search01Icon : RepeatIcon} size={32} className="text-gray-200 mx-auto mb-3" />
                            <p className="text-sm text-gray-400">
                                {searching
                                    ? `No reversals with ${field === 'old' ? 'an old' : 'a new'} transaction ID matching “${search}”`
                                    : `No ${tab} reversal requests`}
                            </p>
                        </div>
                    )}

                    {/* Pagination */}
                    {data.pages > 1 && (
                        <div className="flex items-center justify-between pt-2">
                            <span className="text-xs text-gray-400">Page {page} of {data.pages}</span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    disabled={page <= 1 || loading}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    disabled={page >= data.pages || loading}
                                    onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                                    className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                </section>
            </main>
        </div>
    )
}

export default Index
